import { skipToken } from "@reduxjs/toolkit/query";
import {
  IntegrationProvider,
  type IntegrationConnectionTestResponse,
  type ZoomContactCenterIntegration,
  type ZoomContactCenterIntegrationRequest,
} from "@repo/app-types/entities/integration";
import { buildEntityTag, TagType } from "../helpers/tags.js";
import { api } from "../redux.js";

/**
 * The Zoom Contact Center integration comes from its own route rather than the
 * boot payload: the stored settings carry the encrypted client secret, which no
 * customer response may include, so the API returns a non-secret view instead.
 *
 * The secret travels one way only — every credential write posts it again, and
 * nothing ever reads it back, so the detail view renders a fixed mask.
 */

export interface ZoomContactCenterIntegrationResponse {
  integration: ZoomContactCenterIntegration | null;
}

const ZOOM_TAG_ID = "zoom-contact-center";

export const zoomContactCenterIntegrationApi = api.injectEndpoints({
  endpoints: (builder) => ({
    zoomContactCenterIntegration: builder.query<
      ZoomContactCenterIntegrationResponse,
      void
    >({
      query: () => "/integrations/zoom-contact-center",
      providesTags: () => [
        buildEntityTag(TagType.ZoomContactCenterIntegration, ZOOM_TAG_ID),
      ],
    }),

    /**
     * Probes typed credentials without storing them. Always carries the full
     * credentials because the stored secret is write-only — there is no
     * server-side test to run against a connection the customer isn't editing.
     */
    testZoomContactCenterConnection: builder.mutation<
      IntegrationConnectionTestResponse,
      ZoomContactCenterIntegrationRequest
    >({
      query: (credentials) => ({
        method: "POST",
        url: "/integrations/test-connection",
        body: {
          provider: IntegrationProvider.ZOOM_CONTACT_CENTER,
          ...credentials,
        },
      }),
    }),

    connectZoomContactCenterIntegration: builder.mutation<
      ZoomContactCenterIntegration,
      ZoomContactCenterIntegrationRequest,
      { integration: ZoomContactCenterIntegration }
    >({
      query: (credentials) => ({
        method: "POST",
        url: "/integrations",
        body: {
          provider: IntegrationProvider.ZOOM_CONTACT_CENTER,
          ...credentials,
        },
      }),
      transformResponse: (response) => response.integration,
      invalidatesTags: () => [
        buildEntityTag(TagType.ZoomContactCenterIntegration, ZOOM_TAG_ID),
      ],
    }),

    updateZoomContactCenterIntegration: builder.mutation<
      ZoomContactCenterIntegration,
      {
        integrationId: string;
        credentials: ZoomContactCenterIntegrationRequest;
      },
      { integration: ZoomContactCenterIntegration }
    >({
      query: ({ integrationId, credentials }) => ({
        method: "PATCH",
        url: `/integrations/${integrationId}`,
        body: credentials,
      }),
      transformResponse: (response) => response.integration,
      invalidatesTags: () => [
        buildEntityTag(TagType.ZoomContactCenterIntegration, ZOOM_TAG_ID),
      ],
    }),

    setZoomContactCenterIntegrationEnabled: builder.mutation<
      ZoomContactCenterIntegration,
      { integrationId: string; isEnabled: boolean },
      { integration: ZoomContactCenterIntegration }
    >({
      // Shares the credential route: the union body means pausing never has to
      // carry the client secret.
      query: ({ integrationId, isEnabled }) => ({
        method: "PATCH",
        url: `/integrations/${integrationId}`,
        body: { is_enabled: isEnabled },
      }),
      transformResponse: (response) => response.integration,
      invalidatesTags: () => [
        buildEntityTag(TagType.ZoomContactCenterIntegration, ZOOM_TAG_ID),
      ],
    }),

    disconnectZoomContactCenterIntegration: builder.mutation<
      void,
      { integrationId: string }
    >({
      query: ({ integrationId }) => ({
        method: "DELETE",
        url: `/integrations/${integrationId}`,
      }),
      invalidatesTags: () => [
        buildEntityTag(TagType.ZoomContactCenterIntegration, ZOOM_TAG_ID),
      ],
    }),
  }),
});

/** Subscribes to the status only when Zoom CC is actually on for this company. */
export function useZoomContactCenterIntegration(enabled: boolean) {
  return zoomContactCenterIntegrationApi.useZoomContactCenterIntegrationQuery(
    enabled ? undefined : skipToken,
    { refetchOnMountOrArgChange: true },
  );
}

export const {
  useConnectZoomContactCenterIntegrationMutation,
  useDisconnectZoomContactCenterIntegrationMutation,
  useSetZoomContactCenterIntegrationEnabledMutation,
  useTestZoomContactCenterConnectionMutation,
  useUpdateZoomContactCenterIntegrationMutation,
} = zoomContactCenterIntegrationApi;
